import { on } from "@ngrx/store";
import { CommentWithRepliesFragment, JobDetailsV2Fragment } from "graphql.generated";
import { cloneDeep } from "lodash";

import { Delta } from "quill";

import { LoadingState } from "src/app/utilities/state.util";

import { JobToolSubscriptionActions } from "../job-state/job-tool-subscription.actions";
import { JobToolActions } from "../job-tool.actions";
import { JobToolState } from "../job-tool.reducer";

import { JobActivityActions } from "./job-activity.actions";
import { EditableComment, jobActivityInitialState } from "./job-activity.state";

export const jobActivityReducers = [
  on(JobToolActions.jobLoaded, (state: JobToolState, { job }): JobToolState => {
    return {
      ...state,
      comments: job?.comments?.comments?.map((c) => getEditableComment(c)) || [],
      addComment: {
        ...jobActivityInitialState.addComment,
      }
    }
  }),
  on(JobToolActions.fetchJobSuccess, (state: JobToolState, { job }): JobToolState => {
    return {
      ...state,
      comments: job?.comments?.comments?.map((c) => getEditableComment(c)) || [],
    }
  }),
  on(JobActivityActions.addCommentContentsChanged, (state: JobToolState, { contents }): JobToolState => {
    return {
      ...state,
      addComment: {
        ...state.addComment,
        contents,
      }
    };
  }),
  on(JobActivityActions.addCommentModeSet, (state: JobToolState, commentMode): JobToolState => {

    let subject = state.addComment.subject;

    if (!commentMode.showSubject) {
      subject = new Delta();
    }

    return {
      ...state,
      addComment: {
        ...state.addComment,
        commentModeId: commentMode.id,
        subject,
      },
    };
  }),
  on(
    JobActivityActions.addCommentSubjectChanged,
    JobActivityActions.addCommentTemplateSubjectSet,
    (state: JobToolState, { subject }): JobToolState => {
    return {
      ...state,
      addComment: {
        ...state.addComment,
        subject,
      },
    };
  }),
  on(JobActivityActions.addCommentFilesAttached, (state: JobToolState, { files }): JobToolState => {
    return {
      ...state,
      addComment: {
        ...state.addComment,
        pendingFiles: [...state.addComment.pendingFiles, ...files],
      },
    };
  }),
  on(JobActivityActions.addCommentFileRemoved, (state: JobToolState, { fileId }): JobToolState => {
    return {
      ...state,
      addComment: {
        ...state.addComment,
        pendingFiles: state.addComment.pendingFiles.filter((f) => f.id !== fileId),
      },
    };
  }),
  on(JobActivityActions.newCommentAdded, (state: JobToolState, { comment }): JobToolState => {
    return {
      ...state,
      addComment: {
        ...jobActivityInitialState.addComment,
        commentModeId: state.addComment.commentModeId,
      },
      comments: [ comment, ...cloneDeep(state.comments) ],
    }
  }),
  on(JobActivityActions.editCommentButtonClicked, (state: JobToolState, { commentId, parentId }): JobToolState => {

    const comment = findComment({
      comments: state.comments,
      commentId,
      parentId,
    })

    const editCommentContents = cloneDeep(state.editCommentContents);

    editCommentContents[commentId] = cloneDeep(comment.contents);

    return {
      ...state,
      editCommentContents,
    }
  }),
  on(
    JobActivityActions.saveCommentEditsButtonClicked,
    JobActivityActions.commentEditEnterKeyPressed,
    (state: JobToolState, { commentId, parentId }): JobToolState => {

      const comments = cloneDeep(state.comments);

      const comment = findComment({
        comments,
        commentId,
        parentId,
      });

      const editCommentContents = cloneDeep(state.editCommentContents);

      comment.contents = editCommentContents[commentId];
      comment.callState = LoadingState.MUTATING;

      delete editCommentContents[commentId];

      return {
        ...state,
        comments,
        editCommentContents,
      };
    },
  ),
  on(JobActivityActions.cancelCommentEditsButtonClicked, (state: JobToolState, { commentId }): JobToolState => {

    const editCommentContents = cloneDeep(state.editCommentContents);

    delete editCommentContents[commentId];

    return {
      ...state,
      editCommentContents,
    };
  }),
  on(JobActivityActions.commentEditContentChanged, (state: JobToolState, { commentId, content }): JobToolState => {

    const editCommentContents = cloneDeep(state.editCommentContents);
    editCommentContents[commentId] = content;

    return {
      ...state,
      editCommentContents,
    };
  }),
  on(
    JobActivityActions.replyInputFocused,
    JobActivityActions.replyButtonClicked,
    (state: JobToolState, { commentId }): JobToolState => {

    const addReplyContents = cloneDeep(state.addReplyContents);
    addReplyContents[commentId] = new Delta();

    const addReplyPendingFiles = cloneDeep(state.addReplyPendingFiles);
    addReplyPendingFiles[commentId] = [];

    return {
      ...state,
      addReplyContents,
      addReplyPendingFiles,
    };
    },
  ),
  on(JobActivityActions.replyInputContentChanged, (state: JobToolState, { commentId, content }): JobToolState => {

    const addReplyContents = cloneDeep(state.addReplyContents);
    addReplyContents[commentId] = content;

    return {
      ...state,
      addReplyContents,
    };
  }),
  on(JobActivityActions.replyFilesAttached, (state: JobToolState, { commentId, files }): JobToolState => {
    const addReplyPendingFiles = cloneDeep(state.addReplyPendingFiles);
    addReplyPendingFiles[commentId] = [...(addReplyPendingFiles[commentId] || []), ...files];

    return {
      ...state,
      addReplyPendingFiles,
    };
  }),
  on(JobActivityActions.replyFileRemoved, (state: JobToolState, { commentId, fileId }): JobToolState => {
    const addReplyPendingFiles = cloneDeep(state.addReplyPendingFiles);
    addReplyPendingFiles[commentId] = (addReplyPendingFiles[commentId] || []).filter((f) => f.id !== fileId);

    return {
      ...state,
      addReplyPendingFiles,
    };
  }),
  on(
    JobActivityActions.replyAdded,
    (state: JobToolState, { commentId, parentId, comment }): JobToolState => {

      const comments = cloneDeep(state.comments);

      if (parentId) {
        comments.find((c) => c.id === parentId).replies.push(comment);
      } else {
        comments.find((c) => c.id === commentId).replies.push(comment);
      }

      const addReplyContents = cloneDeep(state.addReplyContents);
      delete addReplyContents[commentId];

      const addReplyPendingFiles = cloneDeep(state.addReplyPendingFiles);
      delete addReplyPendingFiles[commentId];

      return {
        ...state,
        comments,
        addReplyContents,
        addReplyPendingFiles,
      }
    },
  ),
  on(JobActivityActions.cancelReplyButtonClicked, (state: JobToolState, { commentId }): JobToolState => {

    const addReplyContents = cloneDeep(state.addReplyContents);
    delete addReplyContents[commentId];

    const addReplyPendingFiles = cloneDeep(state.addReplyPendingFiles);
    delete addReplyPendingFiles[commentId];

    return {
      ...state,
      addReplyContents,
      addReplyPendingFiles,
    };
  }),
  on(JobActivityActions.toggleExpandRepliesButtonClicked, (state: JobToolState, { commentId }): JobToolState => {

    const repliesCollapsed = { ...state.repliesCollapsed };

    if (commentId in repliesCollapsed) {
      delete repliesCollapsed[commentId];
    } else {
      repliesCollapsed[commentId] = true;
    }

    return {
      ...state,
      repliesCollapsed,
    };
  }),
  on(JobActivityActions.createCommentSuccess, (state: JobToolState, { newComment }): JobToolState => {

    const comments = cloneDeep(state.comments);

    let existingComment: EditableComment;

    if (newComment.isReply) {
      const parent = comments.find((c) => c.threadId === newComment.thread.id);
      existingComment = parent.replies.find((r) => r.id === newComment.id);
    } else {
      existingComment = comments.find((c) => c.id === newComment.id);
    }

    // Do not add comment, this is handled below in response to `JobToolSubscriptionActions.commentCreated`
    if (!existingComment) {
      return state;
    }

    existingComment.callState = LoadingState.MUTATED;
    existingComment.createdAt = newComment.createdAt;
    existingComment.artifacts = newComment.artifacts;

    return {
      ...state,
      comments,
    }
  }),
  on(JobToolSubscriptionActions.commentCreated, (state: JobToolState, { comment: newComment }): JobToolState => {

    const comments = cloneDeep(state.comments);

    let existingComment: EditableComment;

    let parent: EditableComment;

    if (newComment.isReply) {
      parent = comments.find((c) => c.threadId === newComment.thread.id);
      existingComment = parent?.replies.find((r) => r.id === newComment.id);
    } else {
      existingComment = comments.find((c) => c.id === newComment.id);
    }

    // Do not update existing comment's call state, this is handled above in `JobActivityActions.createCommentSuccess`
    if (existingComment) {
      return state;
    }

    if (newComment.isReply && parent) {
      parent.replies.push(getEditableComment(newComment, parent.id));
    } else {
      comments.push(getEditableComment(newComment));
    }

    return {
      ...state,
      comments,
    }
  }),
  on(JobActivityActions.updateCommentSuccess, (state: JobToolState, { commentId, parentId, comment: savedComment }): JobToolState => {

    const comments = cloneDeep(state.comments);

    const comment = findComment({ comments, commentId, parentId });

    // The mutation sends contents with template variables resolved, so replace the
    // optimistic editor Delta (which still holds variable embeds) with what was saved
    if (savedComment) {
      comment.contents = getCommentContents(savedComment);
    }
    comment.callState = LoadingState.MUTATED;

    return {
      ...state,
      comments,
    }
  }),
  on(
    JobActivityActions.deleteCommentButtonClicked,
    JobActivityActions.confirmDeleteCommentButtonClicked,
    (state: JobToolState, { commentId, parentId }): JobToolState => {

      const comments = cloneDeep(state.comments);

      const comment = findComment({
        comments,
        commentId,
        parentId,
      });

      comment.callState = LoadingState.DELETING;

      return {
        ...state,
        comments,
      }
    },
  ),
  on(
    JobToolSubscriptionActions.commentDeleted,
    (state: JobToolState, { comment }): JobToolState => {

      let comments = cloneDeep(state.comments);

      if (comment.isReply) {
      const parent = comments.find((c) => c.threadId === comment.thread.id);
        parent.replies = parent.replies.filter((r) => r.id !== comment.id);
      } else {
        comments = comments.filter((c) => c.id !== comment.id);
      }

      return {
        ...state,
        comments,
      }
    },
  ),
  on(JobActivityActions.addCommentAttachmentsUploadError, (state: JobToolState, { commentId, error }): JobToolState => {

    const comments = cloneDeep(state.comments);
    const comment = comments.find((c) => c.id === commentId);

    if (comment) {
      comment.callState = { error: error?.message || 'Failed to upload attachments' };
    }

    return {
      ...state,
      comments,
    };
  }),
  on(JobActivityActions.replyAttachmentsUploadError, (state: JobToolState, { commentId, parentId, error }): JobToolState => {

    const comments = cloneDeep(state.comments);
    const comment = findComment({ comments, commentId, parentId });

    if (comment) {
      comment.callState = { error: error?.message || 'Failed to upload attachments' };
    }

    return {
      ...state,
      comments,
    };
  }),
  on(
    JobActivityActions.updateCommentError,
    JobActivityActions.deleteCommentError,
    (state: JobToolState, action): JobToolState => {

      const { commentId, parentId, error } = action;

      const comments = cloneDeep(state.comments);

      const comment = findComment({
        comments,
        commentId,
        parentId,
      });

      comment.callState = { error };

      const editCommentContents = cloneDeep(state.editCommentContents);

      if (action.type === JobActivityActions.updateCommentError.type) {
        editCommentContents[commentId] = cloneDeep(comment.contents);
      }

      return {
        ...state,
        comments,
        editCommentContents,
      }
    },
  ),
];

function getEditableComment(comment: JobDetailsV2Fragment['comments']['comments'][number], parentId?: string): EditableComment {

  const replies: EditableComment[] = [];

  for (const reply of comment.replies || []) {
    replies.push(getEditableComment(reply, comment.id));
  }

  return {
    id: comment.id,
    parentId,
    contents: getCommentContents(comment),
    authorName: comment.authorName,
    author: comment.author,
    threadId: comment.thread?.id,
    modeId: comment.thread?.mode,
    subject: getCommentSubject(comment.thread?.subject),
    replies,
    createdAt: comment.createdAt,
    callState: LoadingState.LOADED,
    metadata: comment.metadata,
    attributes: comment.attributes,
    artifacts: comment.artifacts?.filter(Boolean),
  }
}

export function findComment(input: { comments: EditableComment[]; commentId: string; parentId?: string }) {

  const { comments, commentId, parentId } = input;

  if (!parentId) {
    return comments.find((c) => c.id === commentId);
  }

  const parent = comments.find((c) => c.id === parentId);

  return parent.replies.find((c) => c.id === commentId);
}

export function getCommentContents(comment: CommentWithRepliesFragment): Delta {
  return comment.contents ? JSON.parse(comment.contents) as Delta : new Delta({ ops: [ { insert: comment.text } ] });
}

// Requirement: Resolve template subject into a Quill Delta for the subject editor.
export function getCommentSubject(subject: string | null | undefined): Delta {
  if (!subject) return new Delta();

  try {
    const parsed = JSON.parse(subject);
    return typeof parsed === 'string'
      ? new Delta({ ops: [{ insert: parsed }] })
      : (parsed as Delta);
  } catch {
    return new Delta({ ops: [{ insert: subject }] });
  }
}

